import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ChatPanel } from "@/components/ChatPanel";
import { GroupChatPanel } from "@/components/GroupChatPanel";
import { toast } from "sonner";
import { Hash, Users2, Plus, Eye, Trash2 } from "lucide-react";

const NewGroupDialog = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);

  const { data: members = [] } = useQuery({
    queryKey: ["chat-members"],
    queryFn: async () => (await api.get("/chat/members")).data,
    enabled: open,
  });
  const isLeader = ["founder", "admin"].includes(user?.role);
  // Everyone can reach the Founder/Head for a call, and leaders can reach everyone.
  const options = members.filter((m) => m.user_id !== user?.user_id);

  const create = useMutation({
    mutationFn: () => api.post("/chat/groups", { name: name.trim(), member_ids: selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-groups"] });
      toast.success("Group created.");
      setOpen(false); setName(""); setSelected([]);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create group."),
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const removeMember = useMutation({
    mutationFn: (id) => api.delete(`/chat/members/${id}`),
    onSuccess: (_d, id) => {
      setSelected((s) => s.filter((x) => x !== id));
      qc.invalidateQueries({ queryKey: ["chat-members"] });
      toast.success("Removed from the directory.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove."),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-group-btn" className="w-full rounded-lg bg-navy text-white hover:bg-navy-900">
          <Plus className="mr-2 h-4 w-4" /> New Group
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="new-group-dialog">
        <DialogHeader><DialogTitle className="font-heading text-navy">Create a temporary group</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Group name</Label>
            <Input data-testid="group-name-input" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. April Invoice Batch" />
          </div>
          <div>
            <Label>Add teammates</Label>
            <div className="mt-1 max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {options.length === 0 && <p className="py-4 text-center text-xs text-slate-400">No other teammates available.</p>}
              {options.map((m) => (
                <div key={m.user_id} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50" data-testid={`group-member-${m.user_id}`}>
                  <label className="flex flex-1 cursor-pointer items-center gap-2">
                    <Checkbox checked={selected.includes(m.user_id)} onCheckedChange={() => toggle(m.user_id)} />
                    <span className="text-sm text-navy">{m.name}</span>
                  </label>
                  {isLeader && (
                    <button
                      type="button"
                      title="Remove from directory"
                      data-testid={`remove-member-${m.user_id}`}
                      onClick={() => { if (window.confirm(`Remove "${m.name}" from the directory?`)) removeMember.mutate(m.user_id); }}
                      className="rounded-md p-1 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-400">You'll be added automatically. Discard the group anytime when you're done.</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="group-create-submit" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()} className="rounded-lg bg-navy text-white hover:bg-navy-900">
            {create.isPending ? "Creating…" : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function TeamChat() {
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [active, setActive] = useState("team"); // 'team' | group_id

  const { data: groups = [] } = useQuery({
    queryKey: ["chat-groups"],
    queryFn: async () => (await api.get("/chat/groups")).data,
    refetchInterval: 15000,
  });
  const myGroups = groups.filter((g) => g.is_member && g.active);
  const oversight = groups.filter((g) => g.oversight);

  const ChannelBtn = ({ id, icon: Icon, label, sub, testid }) => (
    <button
      onClick={() => setActive(id)}
      data-testid={testid}
      className={cn("flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
        active === id ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100")}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active === id ? "text-gold" : "text-slate-400")} />
      <span className="flex-1 truncate">{label}</span>
      {sub && <span className={cn("text-[10px]", active === id ? "text-white/60" : "text-slate-400")}>{sub}</span>}
    </button>
  );

  return (
    <div data-testid="team-chat-page">
      <PageHeader title="Team Chat" subtitle="Share work progress, raise issues, and create quick group chats" />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <SectionCard testId="chat-channels" className="p-3">
          <ChannelBtn id="team" icon={Hash} label="Team (everyone)" testid="channel-team" />

          <div className="mt-4 mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">My Groups</div>
          <div className="space-y-1">
            {myGroups.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No groups yet.</p>}
            {myGroups.map((g) => (
              <ChannelBtn key={g.group_id} id={g.group_id} icon={Users2} label={g.name} testid={`channel-${g.group_id}`} />
            ))}
          </div>
          <div className="mt-3"><NewGroupDialog /></div>

          {isAdmin && (
            <>
              <div className="mt-5 mb-1 flex items-center gap-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
                <Eye className="h-3 w-3" /> All Groups (Oversight)
              </div>
              <div className="space-y-1" data-testid="oversight-list">
                {oversight.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No other groups.</p>}
                {oversight.map((g) => (
                  <ChannelBtn key={g.group_id} id={g.group_id} icon={Users2} label={g.name}
                    sub={g.active ? "" : "discarded"} testid={`oversight-${g.group_id}`} />
                ))}
              </div>
            </>
          )}
        </SectionCard>

        <SectionCard className="p-0 overflow-hidden" testId="chat-active">
          {active === "team" ? <ChatPanel /> : <GroupChatPanel groupId={active} onDiscarded={() => setActive("team")} />}
        </SectionCard>
      </div>
    </div>
  );
}
